/*
 * Purpose: Human-readable execution result summaries — SSOT for Results panel overlays
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import type { Node } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBlockTypeLabel } from '@/lib/blockTypes';

export interface ExecutionResultSummary {
  headline: string;
  detail: string | null;
  status: 'success' | 'error' | 'simulated' | 'neutral';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function formatTime(iso: string): string | null {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return null;
  }
}

function openAiContent(output: Record<string, unknown>): string | null {
  const choices = output.choices;
  if (!Array.isArray(choices) || choices.length === 0) return null;
  const first = choices[0] as Record<string, unknown> | undefined;
  const message = first?.message as Record<string, unknown> | undefined;
  const content = message?.content;
  return typeof content === 'string' && content.trim() ? content.trim() : null;
}

/** Plain-English summary for a block's execution output. */
export function summarizeExecutionResult(
  output: unknown,
  node?: Node | null,
  errors?: string[]
): ExecutionResultSummary {
  if (errors && errors.length > 0) {
    return {
      headline: 'This step failed',
      detail: errors.join(' '),
      status: 'error',
    };
  }

  if (output == null) {
    return {
      headline: 'Completed with no output',
      detail: node ? `${getBlockTypeLabel(node.type, node.subtype)} finished without returning data.` : null,
      status: 'neutral',
    };
  }

  if (typeof output === 'string') {
    const trimmed = output.trim();
    return {
      headline: trimmed.length > 72 ? `${trimmed.slice(0, 69)}…` : trimmed,
      detail: trimmed.length > 72 ? trimmed : null,
      status: 'success',
    };
  }

  const record = asRecord(output);
  if (!record) {
    return {
      headline: String(output),
      detail: null,
      status: 'success',
    };
  }

  const simulated = record.simulated === true || record.dryRun === true;
  const status: ExecutionResultSummary['status'] = simulated ? 'simulated' : 'success';
  const simNote = simulated ? ' (simulated — no live external call)' : '';

  const blockType = node ? `${node.type}.${node.subtype}` : String(record.type ?? '');

  // Gmail inbound
  if (record.type === 'gmail_inbound' || blockType === 'trigger.gmail_inbound') {
    const subject = pickString(record, ['subject']);
    const from = pickString(record, ['from']);
    const source = pickString(record, ['source']);
    const headline = subject
      ? `Email received: “${subject}”`
      : 'Gmail trigger fired';
    const parts = [
      from ? `From ${from}` : null,
      source === 'gmail_api' ? 'via your connected Gmail' : source ? `via ${source}` : null,
    ].filter(Boolean);
    return { headline, detail: parts.join(' · ') || null, status };
  }

  // START
  if (record.type === 'start' || blockType === 'start.mission') {
    const started = pickString(record, ['startedAt']);
    return {
      headline: 'Mission run started',
      detail: started ? `Started at ${formatTime(started) ?? started}` : null,
      status,
    };
  }

  // GOAL
  if (record.type === 'goal' || blockType === 'goal.outcome') {
    const outcome = pickString(record, ['outcome']) ?? (node ? getNodeLabel(node) : null);
    return {
      headline: outcome ? `Outcome anchored: ${outcome}` : 'Goal block reached',
      detail: record.identified === false ? 'Set an outcome on the GOAL block for clearer tracking.' : null,
      status,
    };
  }

  // Agent
  if (record.type === 'agent' || blockType === 'agent.assistant') {
    const decision = pickString(record, ['decision']);
    const brief = pickString(record, ['brief']);
    return {
      headline: decision ?? 'Agent step completed',
      detail: brief && brief !== decision ? `Brief: ${brief}` : null,
      status,
    };
  }

  // Drone
  if (record.type === 'drone' || blockType === 'drone.remote') {
    const result = pickString(record, ['result']);
    const task = pickString(record, ['task']);
    return {
      headline: result ?? (task ? `Drone task: ${task}` : 'Drone step completed'),
      detail: task && result !== task ? `Task: ${task}` : null,
      status,
    };
  }

  // Log action
  if (record.logged === true || blockType === 'action.log') {
    const message = pickString(record, ['message']);
    return {
      headline: message ? `Logged: ${message}` : 'Message written to logs',
      detail: `Recorded at ${new Date().toLocaleTimeString()}${simNote}`,
      status,
    };
  }

  // Notification
  if (record.notified === true || blockType === 'action.notification') {
    const message = pickString(record, ['message']);
    return {
      headline: message ? `Notification sent: ${message}` : 'Notification dispatched',
      detail: simNote ? simNote.slice(3) : null,
      status,
    };
  }

  // Storage
  if (record.stored === true || blockType === 'action.storage') {
    const key = pickString(record, ['key']);
    return {
      headline: key ? `Saved output to “${key}”` : 'Output stored locally',
      detail: simulated ? 'Dry run — nothing persisted to storage.' : 'Data saved for later steps or review.',
      status,
    };
  }

  // HTTP
  if (record.url || blockType === 'tool.http') {
    const url = pickString(record, ['url']);
    const method = pickString(record, ['method']) ?? 'GET';
    const responseRec = asRecord(record.response) ?? record;
    const ai = openAiContent(responseRec);
    if (ai) {
      return {
        headline: ai.length > 80 ? `${ai.slice(0, 77)}…` : ai,
        detail: `OpenAI response${simNote}`,
        status,
      };
    }
    if (record.sent === true) {
      return {
        headline: `Webhook POST succeeded`,
        detail: url ?? null,
        status,
      };
    }
    if (record.skipped === true) {
      return {
        headline: 'HTTP step skipped',
        detail: pickString(record, ['reason']) ?? 'No URL configured on this block.',
        status: 'neutral',
      };
    }
    return {
      headline: simulated ? `HTTP ${method} simulated` : `HTTP ${method} completed`,
      detail: url ? `Called ${url}${simNote}` : pickString(record, ['message']) ?? null,
      status,
    };
  }

  // Transform / conditional / delay
  if (blockType === 'tool.transform') {
    return {
      headline: 'Data transformed for the next step',
      detail: simulated ? 'Transformation ran in dry-run mode.' : 'Output passed to downstream blocks.',
      status,
    };
  }
  if (blockType === 'tool.conditional') {
    return {
      headline: 'Branch evaluated',
      detail: 'Flow continued on the matching path.',
      status,
    };
  }
  if (blockType === 'tool.delay' || record.delaySkipped === true) {
    return {
      headline: simulated ? 'Delay skipped in dry run' : 'Wait step completed',
      detail: null,
      status,
    };
  }

  // Branded / tube
  const message = pickString(record, ['message']);
  if (message) {
    return {
      headline: message.length > 100 ? `${message.slice(0, 97)}…` : message,
      detail: message.length > 100 ? message : null,
      status,
    };
  }

  // Generic fallbacks
  for (const key of ['summary', 'result', 'text', 'decision', 'body']) {
    const text = pickString(record, [key]);
    if (text) {
      return {
        headline: text.length > 90 ? `${text.slice(0, 87)}…` : text,
        detail: text.length > 90 ? text : null,
        status,
      };
    }
  }

  const label = node ? getNodeLabel(node) : 'Block';
  return {
    headline: `${label} completed successfully`,
    detail: 'Expand raw JSON below for full payload.',
    status,
  };
}

export function formatExecutionResultJson(output: unknown): string {
  if (output == null) return 'null';
  if (typeof output === 'string') return output;
  try {
    return JSON.stringify(output, null, 2);
  } catch {
    return String(output);
  }
}
